import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { MUIDataTableState } from 'mui-datatables';
import { useTranslation } from 'react-i18next';

import { Table, customHeadRender } from 'components/table';
import { BankTransaction } from 'store/applications/types';
import { BankTransactionsColumnNames } from 'core/types';
import { parseMoney } from 'utils';
import { useApplications } from 'store/applications/hooks';
import { mapAppRegionToSupportedCurrency } from 'core/utils';

interface TransactionsTableProps {
  transactions: BankTransaction[];
  total: number;
  activePage: number;
  setActivePage: Dispatch<SetStateAction<number>>;
}

const TransactionsTable: FC<TransactionsTableProps> = ({ transactions, total, activePage, setActivePage }) => {
  const { t } = useTranslation();
  const { applicationDetails } = useApplications();

  const appRegion = applicationDetails?.region;

  const columns = useMemo(
    () => [
      {
        name: BankTransactionsColumnNames.AMOUNT,
        label: t('pages.lead.sections.bankData.transactionsTable.columns.amount'),
        options: {
          customHeadRender,
          customBodyRender: (value: number) =>
            value ? parseMoney(value, mapAppRegionToSupportedCurrency(appRegion)) : null,
        },
      },
      {
        name: BankTransactionsColumnNames.CURRENCY,
        label: t('pages.lead.sections.bankData.transactionsTable.columns.currency'),
        options: { customHeadRender },
      },
      {
        name: BankTransactionsColumnNames.CREDIT_DEBIT_INDICATOR,
        label: t('pages.lead.sections.bankData.transactionsTable.columns.type'),
        options: { customHeadRender },
      },
      {
        name: BankTransactionsColumnNames.BOOKING_DATE_TIME,
        label: t('pages.lead.sections.bankData.transactionsTable.columns.date'),
        options: {
          customHeadRender,
          customBodyRender: (value: string) => (value ? new Date(value).toLocaleDateString() : null),
        },
      },
    ],
    [appRegion, t],
  );

  const onTableChange = useCallback(
    (action: string, tableState: MUIDataTableState) => {
      if (action === 'changePage') {
        setActivePage(tableState.page);
      }
    },
    [setActivePage],
  );

  return (
    <Table<BankTransaction>
      columns={columns}
      data={transactions}
      count={total}
      page={activePage}
      rowsPerPage={10}
      onTableChange={onTableChange}
    />
  );
};

export default TransactionsTable;
